import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MuiButton from '@material-ui/core/Button';
import MuiIconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import { fade, darken } from '@material-ui/core/styles/colorManipulator';

const MUI_COLORS = ['default', 'inherit', 'primary', 'secondary'];

export const COLORS = [
  ...MUI_COLORS,
  'primaryBlue',
  'accentBlue',
  'accentBlue2',
  'accentBlue20',
  'lightGray',
  'primaryGray',
  'red',
  'white',
  'accentGreen',
  'orange',
];

const styles = theme => ({
  root: {
    borderRadius: props => (props.round ? '100px' : undefined),
    '&:focus': {
      textDecoration: 'none',
    },
  },
  paddingDense: {
    padding: theme.spacing(0.5, 1),
  },
  paddingNone: {
    padding: 0,
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
  },
  paddingSquished: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  // Contained
  variantContained: {
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.common.white, // This needs to stay until a:hover overrides have been removed.
    },
  },
  variantContainedPrimaryBlue: {
    backgroundColor: theme.palette.facet.primaryBlue,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.common.white,
      backgroundColor: darken(
        theme.palette.facet.primaryBlue,
        theme.palette.action.hoverOpacity,
      ),
    },
  },
  variantContainedAccentBlue: {
    backgroundColor: theme.palette.facet.accentBlue,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.common.white,
      backgroundColor: darken(
        theme.palette.facet.accentBlue,
        theme.palette.action.hoverOpacity,
      ),
    },
  },
  variantContainedAccentBlue2: {
    backgroundColor: theme.palette.facet.accentBlue2,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.common.white,
      backgroundColor: darken(
        theme.palette.facet.accentBlue2,
        theme.palette.action.hoverOpacity,
      ),
    },
  },
  variantContainedAccentBlue16: {
    backgroundColor: theme.palette.facet.accentBlue16,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.common.white,
      backgroundColor: darken(
        theme.palette.facet.accentBlue16,
        theme.palette.action.hoverOpacity,
      ),
    },
  },
  variantContainedAccentBlue20: {
    backgroundColor: theme.palette.facet.accentBlue20,
    color: theme.palette.facet.primaryBlue,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.facet.primaryBlue,
      backgroundColor: darken(
        theme.palette.facet.accentBlue20,
        theme.palette.action.hoverOpacity,
      ),
    },
  },
  variantContainedLightGray: {
    backgroundColor: theme.palette.facet.lightGray,
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.primary.main,
      cursor: 'pointer',
      backgroundColor: darken(
        theme.palette.facet.lightGray,
        theme.palette.action.hoverOpacity,
      ),
    },
  },
  variantContainedPrimaryGray: {
    backgroundColor: theme.palette.facet.primaryGray,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      backgroundColor: darken(
        theme.palette.facet.primaryGray,
        theme.palette.action.hoverOpacity,
      ),
    },
  },
  variantContainedRed: {
    backgroundColor: theme.palette.facet.red,
    color: theme.palette.common.white,
    '&:hover': {
      cursor: 'pointer',
      textDecoration: 'none',
      backgroundColor: darken(
        theme.palette.facet.red,
        theme.palette.action.hoverOpacity,
      ),
    },
  },
  variantContainedOrange: {
    backgroundColor: theme.palette.facet.orange,
    color: theme.palette.common.white,
    '&:hover': {
      cursor: 'pointer',
      textDecoration: 'none',
      backgroundColor: darken(
        theme.palette.facet.orange,
        theme.palette.action.hoverOpacity,
      ),
    },
  },
  variantContainedWhite: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.primary.main,
      cursor: 'pointer',
      backgroundColor: darken(
        theme.palette.common.white,
        theme.palette.action.hoverOpacity,
      ),
    },
  },
  // Outlined
  variantOutlinedPrimary: {
    color: theme.palette.primary.main,
    border: `1px solid ${fade(theme.palette.primary.main, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: fade(
        theme.palette.primary.main,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedSecondary: {
    color: theme.palette.secondary.main,
    border: `1px solid ${fade(theme.palette.secondary.main, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.secondary.main}`,
      backgroundColor: fade(
        theme.palette.secondary.main,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedPrimaryBlue: {
    color: theme.palette.facet.primaryBlue,
    border: `1px solid ${fade(theme.palette.facet.primaryBlue, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.primaryBlue}`,
      backgroundColor: fade(
        theme.palette.facet.primaryBlue,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedAccentBlue: {
    color: theme.palette.facet.accentBlue,
    border: `1px solid ${fade(theme.palette.facet.accentBlue, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.accentBlue}`,
      backgroundColor: fade(
        theme.palette.facet.accentBlue,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedAccentBlue2: {
    color: theme.palette.facet.accentBlue2,
    border: `1px solid ${fade(theme.palette.facet.accentBlue2, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.accentBlue2}`,
      backgroundColor: fade(
        theme.palette.facet.accentBlue2,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedAccentBlue16: {
    color: theme.palette.facet.accentBlue16,
    border: `1px solid ${fade(theme.palette.facet.accentBlue16, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.accentBlue16}`,
      backgroundColor: fade(
        theme.palette.facet.accentBlue16,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedLightGray: {
    color: theme.palette.facet.lightGray,
    border: `1px solid ${fade(theme.palette.facet.lightGray, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.lightGray}`,
      backgroundColor: fade(
        theme.palette.facet.lightGray,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedPrimaryGray: {
    color: theme.palette.facet.primaryGray,
    border: `1px solid ${fade(theme.palette.facet.primaryGray, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.primaryGray}`,
      backgroundColor: fade(
        theme.palette.facet.primaryGray,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedRed: {
    color: theme.palette.facet.red,
    border: `1px solid ${fade(theme.palette.facet.red, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.red}`,
      backgroundColor: fade(
        theme.palette.facet.red,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedWhite: {
    color: theme.palette.common.white,
    border: `1px solid ${fade(theme.palette.common.white, 0.5)}`,
    '&:hover': {
      border: `1px solid ${theme.palette.common.white}`,
      backgroundColor: fade(
        theme.palette.common.white,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  // Text
  variantTextPrimary: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.primary.main, // This needs to stay until a:hover overrides have been removed.
    },
  },
  variantTextSecondary: {
    color: theme.palette.secondary.main,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.secondary.main, // This needs to stay until a:hover overrides have been removed.
    },
  },
  variantTextPrimaryBlue: {
    color: theme.palette.facet.primaryBlue,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.primaryBlue,
    },
  },
  variantTextAccentBlue: {
    color: theme.palette.facet.accentBlue,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.accentBlue,
    },
  },
  variantTextAccentBlue2: {
    color: theme.palette.facet.accentBlue2,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.accentBlue2,
    },
  },
  variantTextAccentBlue16: {
    color: theme.palette.facet.accentBlue16,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.accentBlue16,
    },
  },
  variantTextLightGray: {
    color: theme.palette.facet.lightGray,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.lightGray,
    },
  },
  variantTextPrimaryGray: {
    color: theme.palette.facet.primaryGray,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.primaryGray,
    },
  },
  variantTextRed: {
    color: theme.palette.facet.red,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.red,
    },
  },
  variantTextOrange: {
    color: theme.palette.facet.orange,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.orange,
    },
  },
  variantTextWhite: {
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.common.white,
    },
  },
  variantTextAccentGreen: {
    color: theme.palette.facet.accentGreen,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.accentGreen,
    },
  },
  // Underlined
  variantUnderlined: {
    textDecoration: 'underline',
    '&:focus': {
      textDecoration: 'underline',
    },
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

const Button = React.forwardRef(
  (
    {
      classes,
      className,
      color,
      buttonType,
      padding,
      round: _round,
      variant,
      ...rest
    },
    ref,
  ) => {
    const paddingClass = classNames({
      [classes.paddingDense]: padding === 'dense',
      [classes.paddingNone]: padding === 'none',
      [classes.paddingSquished]: padding === 'squished',
    });

    const hasTextStyling = variant === 'text' || variant === 'underlined' || variant === 'icon';

    const variantClass = classNames({
      [classes.variantContained]: variant === 'contained',
      [classes.variantContainedPrimaryBlue]:
        variant === 'contained' && color === 'primaryBlue',
      [classes.variantContainedAccentBlue]:
        variant === 'contained' && color === 'accentBlue',
      [classes.variantContainedAccentBlue2]:
        variant === 'contained' && color === 'accentBlue2',
      [classes.variantContainedAccentBlue16]:
        variant === 'contained' && color === 'accentBlue16',
      [classes.variantContainedAccentBlue20]:
        variant === 'contained' && color === 'accentBlue20',
      [classes.variantContainedLightGray]:
        variant === 'contained' && color === 'lightGray',
      [classes.variantContainedPrimaryGray]:
        variant === 'contained' && color === 'primaryGray',
      [classes.variantContainedRed]: variant === 'contained' && color === 'red',
      [classes.variantContainedOrange]:
        variant === 'contained' && color === 'orange',
      [classes.variantContainedWhite]:
        variant === 'contained' && color === 'white',
      // Variant Text
      [classes.variantUnderlined]: variant === 'underlined',
      [classes.variantTextPrimary]: hasTextStyling && color === 'primary',
      [classes.variantTextSecondary]: hasTextStyling && color === 'secondary',
      [classes.variantTextPrimaryBlue]:
        hasTextStyling && color === 'primaryBlue',
      [classes.variantTextAccentBlue]: hasTextStyling && color === 'accentBlue',
      [classes.variantTextAccentBlue2]:
        hasTextStyling && color === 'accentBlue2',
      [classes.variantTextAccentBlue16]:
        hasTextStyling && color === 'accentBlue16',
      [classes.variantTextLightGray]: hasTextStyling && color === 'lightGray',
      [classes.variantTextPrimaryGray]:
        hasTextStyling && color === 'primaryGray',
      [classes.variantTextRed]: hasTextStyling && color === 'red',
      [classes.variantTextOrange]: hasTextStyling && color === 'orange',
      [classes.variantTextWhite]: hasTextStyling && color === 'white',
      [classes.variantTextAccentGreen]:
        hasTextStyling && color === 'accentGreen',
      // Variant Outlined
      [classes.variantOutlinedPrimaryBlue]:
        variant === 'outlined' && color === 'primaryBlue',
      [classes.variantOutlinedAccentBlue]:
        variant === 'outlined' && color === 'accentBlue',
      [classes.variantOutlinedAccentBlue2]:
        variant === 'outlined' && color === 'accentBlue2',
      [classes.variantOutlinedAccentBlue16]:
        variant === 'outlined' && color === 'accentBlue16',
      [classes.variantOutlinedLightGray]:
        variant === 'outlined' && color === 'lightGray',
      [classes.variantOutlinedPrimaryGray]:
        variant === 'outlined' && color === 'primaryGray',
      [classes.variantOutlinedRed]: variant === 'outlined' && color === 'red',
      [classes.variantOutlinedWhite]:
        variant === 'outlined' && color === 'white',
    });

    let ButtonComponent;

    let muiVariant;
    switch (variant) {
      case 'underlined':
        muiVariant = 'text';
        break;
      case 'icon':
        muiVariant = null;
        break;
      default:
        muiVariant = variant;
        break;
    }

    switch (variant) {
      case 'icon':
        ButtonComponent = MuiIconButton;
        break;
      default:
        ButtonComponent = MuiButton;
        break;
    }

    return (
      <ButtonComponent
        className={classNames(
          classes.root,
          paddingClass,
          variantClass,
          className,
        )}
        color={MUI_COLORS.includes(color) ? color : 'inherit'}
        ref={ref}
        variant={muiVariant}
        {...rest}
      />
    );
  },
);

Button.defaultProps = {
  className: '',
  color: 'inherit',
  padding: 'default',
  round: false,
  variant: 'text',
};

Button.propTypes = {
  classes: PropTypes.object.isRequired,
  color: PropTypes.oneOf(COLORS),
  padding: PropTypes.oneOf(['default', 'none', 'dense', 'squished']),
  round: PropTypes.bool,
  variant: PropTypes.oneOf([
    'icon',
    'text',
    'outlined',
    'contained',
    'underlined', // Exactly the same as text but with an underline
  ]),
};
export default withStyles(styles)(Button);
